/**
 * WizardShell — shared frame for the three signing wizards.
 *
 * Provides a consistent top bar with a Back button, the wizard title,
 * and a progress indicator. Children render the active step.
 */

import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { cn } from '@/utils/cn';

export interface WizardShellProps {
  title: string;
  /** Total number of steps (1-indexed display). */
  totalSteps: number;
  /** Current step (1-indexed). */
  currentStep: number;
  /** Optional override for the back behaviour (default: navigate to /). */
  onBack?: () => void;
  children: React.ReactNode;
}

export function WizardShell({ title, totalSteps, currentStep, onBack, children }: WizardShellProps) {
  const { t } = useTranslation('wizards');
  const navigate = useNavigate();
  const handleBack = onBack ?? (() => navigate('/'));

  return (
    <div className="mx-auto w-full max-w-2xl">
      <div className="mb-6 flex items-center justify-between gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          leading={<ArrowLeft className="h-4 w-4 rtl:rotate-180" aria-hidden />}
        >
          {t('common.back')}
        </Button>
        <span className="text-xs text-ink-muted" aria-live="polite">
          {currentStep} / {totalSteps}
        </span>
      </div>

      <h1 className="mb-2 text-2xl font-semibold tracking-tight">{title}</h1>

      <div
        className="mb-6 h-1 w-full overflow-hidden rounded-full bg-surface-raised"
        role="progressbar"
        aria-valuenow={currentStep}
        aria-valuemin={1}
        aria-valuemax={totalSteps}
      >
        <div
          className={cn('h-full bg-spark transition-all duration-300 ease-emphasized')}
          style={{ width: `${Math.min(100, (currentStep / totalSteps) * 100)}%` }}
        />
      </div>

      <div className="rounded-xl border border-border bg-surface p-6 shadow-subtle sm:p-8">
        {children}
      </div>
    </div>
  );
}
